/**
 * Audit template definition — the JSON stored in AuditTemplates.Definition.
 * Templates are data, not code (spec §4): new audit types must require zero
 * code changes. Bump schemaVersion only on breaking shape changes; the
 * renderer/scoring must keep reading every published version.
 */

export const TEMPLATE_SCHEMA_VERSION = 1;

export type QuestionType =
  | 'yes_no'
  | 'yes_no_na'
  | 'short_text'
  | 'long_text'
  | 'number'
  | 'percent'
  | 'date'
  | 'multi_select'
  | 'sample_group';

/**
 * LEGACY show-if on one gate answer (spec §4: simple v1 conditionals) — still
 * read on every published version; since the Conditions page (owner rulings
 * 2026-11-02) a draft's are turned into Show rules (migrateVisibleIf) and the
 * builder edits only `TemplateDefinition.conditions`.
 */
export interface Condition {
  /** Must reference a yes_no question declared earlier in the template. */
  questionId: string;
  equals: 'yes' | 'no';
}

export type SampleFieldType = 'short_text' | 'long_text' | 'yes_no' | 'yes_no_na';

export interface SampleField {
  /** Unique within its sample group. */
  id: string;
  label: string;
  type: SampleFieldType;
  /** Default: yes_no/yes_no_na fields are scored, text fields are not. */
  scored?: boolean;
  /**
   * Conditionally required: the field must be filled only when a sibling
   * yes/no(/na) field in the same entry has the given answer (e.g. "List
   * discrepancies" required only when "Verified and correct" = no).
   * Fields without requiredIf are unconditionally required.
   */
  requiredIf?: { fieldId: string; equals: YesNoNa };
}

/** Store attribute flags a gate question may pre-fill from (spec §4). */
export type StoreAttributeKey = 'hasParking' | 'hasElevators';

export interface Question {
  /** Unique across the whole template. */
  id: string;
  type: QuestionType;
  text: string;
  /**
   * "More detail" (owner ruling 2026-11-02, was a subheading shown above the
   * question): guidance for doing the question, shown UNDER it on the
   * conduct screen only — never on the record, the PDF or reports. The
   * field keeps its stored name so published forms need no republish.
   */
  heading?: string;
  /**
   * Subsection title (owner ruling 2026-11-02): starts a subsection above
   * this question — shown on the conduct screen, the audit record and the
   * PDF, in normal case. It holds until the next question with a title
   * (subsections never cross sections).
   */
  subsection?: string;
  /** Default true. */
  required?: boolean;
  /** Scoring weight, default 1. Ignored for unscored questions. */
  weight?: number;
  /** Failing this question triggers the template's CriticalBehavior (weighted_critical mode). */
  critical?: boolean;
  /**
   * Default: yes_no/yes_no_na are scored; text/number/percent/date/
   * multi_select are not. sample_group scoring comes from its fields.
   */
  scored?: boolean;
  /**
   * Attachment policy. Default 'optional' — any question can take a photo.
   * 'required_on_fail' (owner-approved) = an attachment becomes mandatory
   * only when the question's scored answer is a failure.
   */
  attachments?: 'none' | 'optional' | 'required' | 'required_on_fail';
  /** Allowed attachment kinds; default 'both' (photos or PDFs — owner ruling, Phase 3). */
  attachmentTypes?: 'photo' | 'pdf' | 'both';
  visibleIf?: Condition;
  /** multi_select only. */
  options?: string[];
  /** sample_group only: MAXIMUM number of entries ("verify up to N"). */
  sampleCount?: number;
  /** sample_group only: minimum entries required to submit (default 0). */
  minCount?: number;
  /** sample_group only: fields rendered per entry. */
  fields?: SampleField[];
  /** yes_no gates only: pre-fill from the store record when starting an audit. */
  prefillStoreAttribute?: StoreAttributeKey;
  /**
   * number/percent only: a BUDGET / GOAL question — stores get monthly
   * targets for it on Budgets and Goals (owner ask 2026-10-26; only ticked
   * questions have budgets — src/audit/kpis.ts budgetQuestionIds).
   */
  budget?: boolean;
}

export interface Section {
  /** Unique across the template. */
  id: string;
  title: string;
  /** "How to prepare" text shown under the title. */
  instruction?: string;
  /** Render a free-text notes box at the end of the section. */
  notesField?: boolean;
  visibleIf?: Condition;
  questions: Question[];
}

/** What a rule does to its targets (Conditions page, owner rulings 2026-11-02). */
export type RuleAction = 'show' | 'hide' | 'require' | 'not_require';
/** 'is' for Yes/No(/N/A) triggers; 'includes' / 'not_includes' for multi-select ones. */
export type RuleOp = 'is' | 'includes' | 'not_includes';

export interface RuleTrigger {
  /** A yes_no, yes_no_na or multi_select question that is always shown. */
  questionId: string;
  op: RuleOp;
  /** 'yes' | 'no' | 'na' for 'is'; one of the question's options otherwise. */
  value: string;
}

/**
 * One rule: ONE trigger, MANY targets (question or section ids; Require /
 * Not require take questions only). Several rules on one target: any met
 * rule wins — Show rules OR together, a met Hide beats Show, a met Require
 * beats Not require. Unanswered triggers are never met.
 */
export interface ConditionRule {
  /** Unique among the template's ids. */
  id: string;
  action: RuleAction;
  when: RuleTrigger;
  targets: string[];
}

export interface TemplateDefinition {
  schemaVersion: number;
  name: string;
  auditType: string;
  sections: Section[];
  /** The Conditions page's rules (owner rulings 2026-11-02). */
  conditions?: ConditionRule[];
}

// ---------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------

export type YesNoNa = 'yes' | 'no' | 'na';

/** One sample-group entry: sub-field id → value. */
export type SampleEntryValue = Record<string, string | undefined>;

/**
 * Answer payload per question type:
 *  yes_no / yes_no_na → YesNoNa ('na' only for yes_no_na)
 *  short_text / long_text / date → string (date = ISO yyyy-mm-dd)
 *  number / percent → number
 *  multi_select → string[] (selected options)
 *  sample_group → SampleEntryValue[] (index = SampleIndex)
 */
export type AnswerValue =
  | YesNoNa
  | string
  | number
  | string[]
  | SampleEntryValue[];

/** All answers for an audit, keyed by question id. */
export type AnswerMap = Record<string, AnswerValue | undefined>;

// ---------------------------------------------------------------------------
// Defaults & helpers
// ---------------------------------------------------------------------------

export function isQuestionScored(q: Question): boolean {
  if (q.type === 'sample_group') {
    return (q.fields ?? []).some((f) => isFieldScored(f));
  }
  if (q.scored !== undefined) return q.scored;
  return q.type === 'yes_no' || q.type === 'yes_no_na';
}

/** Does the form produce a score at all? (A DM Visit report doesn't.) */
export function definitionHasScore(def: TemplateDefinition, scoringMode?: string): boolean {
  if (scoringMode === 'none') return false;
  return def.sections.some((s) => (s.questions ?? []).some((q) => isQuestionScored(q)));
}

export function isFieldScored(f: SampleField): boolean {
  if (f.scored !== undefined) return f.scored;
  return f.type === 'yes_no' || f.type === 'yes_no_na';
}

export function questionWeight(q: Question): number {
  return q.weight ?? 1;
}

/** Is a rule's trigger met? Unanswered (or an empty multi-select) ⇒ never met. */
export function triggerMet(t: RuleTrigger, answers: AnswerMap): boolean {
  const answer = Object.prototype.hasOwnProperty.call(answers, t.questionId) ? answers[t.questionId] : undefined;
  if (t.op === 'is') return typeof answer === 'string' && answer === t.value;
  if (!Array.isArray(answer) || answer.length === 0) return false;
  const has = (answer as unknown[]).includes(t.value);
  return t.op === 'includes' ? has : t.op === 'not_includes' ? !has : false;
}

interface TargetRules {
  show: RuleTrigger[];
  hide: RuleTrigger[];
  require: RuleTrigger[];
  notRequire: RuleTrigger[];
}
const ACTION_KEY: Record<RuleAction, keyof TargetRules> = { show: 'show', hide: 'hide', require: 'require', not_require: 'notRequire' };
const RULE_OPS = new Set<string>(['is', 'includes', 'not_includes']);
const indexCache = new WeakMap<TemplateDefinition, Map<string, TargetRules>>();

/**
 * Every rule that applies to each section / question id — the legacy
 * visibleIf counted as a Show rule. Defensive: a malformed rule (hand-edited
 * JSON) is skipped rather than breaking the form, the record or the PDF.
 */
export function rulesByTarget(def: TemplateDefinition): Map<string, TargetRules> {
  const cached = indexCache.get(def);
  if (cached) return cached;
  const index = new Map<string, TargetRules>();
  const add = (id: string, key: keyof TargetRules, t: RuleTrigger) => {
    let entry = index.get(id);
    if (!entry) index.set(id, (entry = { show: [], hide: [], require: [], notRequire: [] }));
    entry[key].push(t);
  };
  for (const s of def.sections ?? []) {
    if (s.visibleIf) add(s.id, 'show', { questionId: s.visibleIf.questionId, op: 'is', value: s.visibleIf.equals });
    for (const q of s.questions ?? []) {
      if (q.visibleIf) add(q.id, 'show', { questionId: q.visibleIf.questionId, op: 'is', value: q.visibleIf.equals });
    }
  }
  for (const r of Array.isArray(def.conditions) ? def.conditions : []) {
    // Own keys only: a hand-edited action like "constructor" must not reach the prototype.
    const key = r && typeof r === 'object' && Object.prototype.hasOwnProperty.call(ACTION_KEY, r.action) ? ACTION_KEY[r.action] : undefined;
    const w = r?.when;
    if (!key || !w || typeof w.questionId !== 'string' || typeof w.value !== 'string' || !RULE_OPS.has(w.op) || !Array.isArray(r.targets)) continue;
    for (const id of new Set(r.targets)) if (typeof id === 'string') add(id, key, w);
  }
  indexCache.set(def, index);
  return index;
}

/**
 * The rules in a usable shape (hand-edited JSON aside): an object with a
 * string id, a known type, a `when` of strings and a list of string targets
 * (duplicates collapsed). An unfinished rule from the builder (no trigger
 * yet, no targets) is kept. The builder works on these only.
 */
export function wellFormedRules(conditions: unknown): ConditionRule[] {
  if (!Array.isArray(conditions)) return [];
  const out: ConditionRule[] = [];
  for (const r of conditions as Partial<ConditionRule>[]) {
    const w = r?.when;
    if (!r || typeof r !== 'object' || typeof r.id !== 'string' || !Object.prototype.hasOwnProperty.call(ACTION_KEY, r.action as string)) continue;
    if (!w || typeof w !== 'object' || typeof w.questionId !== 'string' || typeof w.value !== 'string' || !RULE_OPS.has(w.op as string)) continue;
    if (!Array.isArray(r.targets)) continue;
    out.push({ id: r.id, action: r.action as RuleAction, when: { questionId: w.questionId, op: w.op as RuleOp, value: w.value }, targets: [...new Set(r.targets.filter((t): t is string => typeof t === 'string'))] });
  }
  return out;
}

/** Shown: no Show rule or any met, and no Hide rule met (Hide beats Show). */
function shownBy(rules: TargetRules | undefined, answers: AnswerMap): boolean {
  if (!rules) return true;
  if (rules.show.length && !rules.show.some((t) => triggerMet(t, answers))) return false;
  return !rules.hide.some((t) => triggerMet(t, answers));
}

export function isSectionVisible(def: TemplateDefinition, section: Section, answers: AnswerMap): boolean {
  return shownBy(rulesByTarget(def).get(section.id), answers);
}

export function isQuestionVisible(
  def: TemplateDefinition,
  section: Section,
  q: Question,
  answers: AnswerMap,
): boolean {
  if (!isSectionVisible(def, section, answers)) return false;
  return shownBy(rulesByTarget(def).get(q.id), answers);
}

/**
 * Must this question be answered right now? Its own setting (default
 * required), unless a met Not require rule makes it optional — and a met
 * Require rule makes it required whatever else (Require beats Not require).
 */
export function isQuestionRequired(def: TemplateDefinition, q: Question, answers: AnswerMap): boolean {
  const rules = rulesByTarget(def).get(q.id);
  if (rules?.require.some((t) => triggerMet(t, answers))) return true;
  if (rules?.notRequire.some((t) => triggerMet(t, answers))) return false;
  return q.required !== false;
}

/**
 * A draft's legacy "Show this question" conditions as Show rules (owner
 * ruling 2026-11-02: converted when the next draft is opened, no script):
 * one rule per gate + answer, its targets in form order; visibleIf removed.
 * `newRuleId` mints ids. Returns `def` itself when there is nothing to move.
 */
export function migrateVisibleIf(def: TemplateDefinition, newRuleId: () => string): TemplateDefinition {
  const groups = new Map<string, ConditionRule>();
  const take = (cond: Condition | undefined, target: string) => {
    if (!cond) return;
    const key = `${cond.questionId}\u0000${cond.equals}`;
    let rule = groups.get(key);
    if (!rule) groups.set(key, (rule = { id: newRuleId(), action: 'show', when: { questionId: cond.questionId, op: 'is', value: cond.equals }, targets: [] }));
    rule.targets.push(target);
  };
  for (const s of def.sections) {
    take(s.visibleIf, s.id);
    for (const q of s.questions) take(q.visibleIf, q.id);
  }
  if (groups.size === 0) return def;
  const strip = <T extends { visibleIf?: Condition }>(x: T): T => {
    if (!x.visibleIf) return x;
    const next = { ...x };
    delete next.visibleIf;
    return next;
  };
  return {
    ...def,
    sections: def.sections.map((s) => ({ ...strip(s), questions: s.questions.map(strip) })),
    conditions: [...wellFormedRules(def.conditions), ...groups.values()],
  };
}


/**
 * Which subsection title to show above which of `shown` (the questions of
 * one section actually on screen, in order): above the first shown question
 * of each subsection — so a title on a hidden or filtered-out question still
 * heads the rest of its subsection. Question id → title. Pure.
 */
export function subsectionTitlesFor(section: { questions: readonly Question[] }, shown: readonly Question[]): Map<string, string> {
  const shownIds = new Set(shown.map((q) => q.id));
  const out = new Map<string, string>();
  let current = '';
  let announced = '';
  for (const q of section.questions) {
    // A non-string (a hand-edited row) starts no subsection rather than breaking the screen / PDF.
    const title = typeof q.subsection === 'string' ? q.subsection.trim() : '';
    if (title) {
      current = title;
      announced = '';
    }
    if (current && !announced && shownIds.has(q.id)) {
      out.set(q.id, current);
      announced = current;
    }
  }
  return out;
}
